import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CloneIcon,
  Biotech as BiotechIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import {
  idexxService,
  IdexxLabPanel,
  IdexxRefTest,
} from '../../services/idexxService';

type Modality = 'INHOUSE' | 'REFLAB';

/**
 * Settings → Integrations → IDEXX: manage reusable lab-test panels (bundles).
 * Seeded "system" starters are read-only but can be duplicated into editable
 * clinic panels. Clinic panels are fully editable. Panels show up as one-click
 * chips in the New Lab Request dialog.
 */
const IdexxPanelManager: React.FC = () => {
  const [panels, setPanels] = useState<IdexxLabPanel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);

  // Editor state (null editing = create).
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<IdexxLabPanel | null>(null);
  const [form, setForm] = useState<{
    name: string;
    description: string;
    modality: Modality;
    testCodes: string[];
  }>({ name: '', description: '', modality: 'INHOUSE', testCodes: [] });
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  // Test options for the picker — lazy-loaded the first time the editor opens.
  const [testOptions, setTestOptions] = useState<IdexxRefTest[]>([]);
  const [loadingTests, setLoadingTests] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    setUnauthorized(false);
    try {
      setPanels(await idexxService.listPanels());
    } catch (err: any) {
      if (err.response?.status === 403) setUnauthorized(true);
      else setError(err.response?.data?.message || 'Failed to load lab panels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ensureTestsLoaded = async () => {
    if (testOptions.length > 0 || loadingTests) return;
    setLoadingTests(true);
    try {
      // equineOnly:false so the full in-house (IHD_) menu is available for
      // building panels — IDEXX flags only one in-house test "equine".
      setTestOptions(await idexxService.listTests({ equineOnly: false }));
    } catch {
      /* leave empty; user can still type codes are pre-filled from a clone */
    } finally {
      setLoadingTests(false);
    }
  };

  const openCreate = (modality: Modality = 'INHOUSE') => {
    setEditing(null);
    setForm({ name: '', description: '', modality, testCodes: [] });
    setEditorError(null);
    setEditorOpen(true);
    void ensureTestsLoaded();
  };

  const openEdit = (panel: IdexxLabPanel) => {
    setEditing(panel);
    setForm({
      name: panel.name,
      description: panel.description ?? '',
      modality: panel.modality,
      testCodes: panel.testCodes,
    });
    setEditorError(null);
    setEditorOpen(true);
    void ensureTestsLoaded();
  };

  const openClone = (panel: IdexxLabPanel) => {
    setEditing(null); // clone creates a NEW clinic panel
    setForm({
      name: `${panel.name} (copy)`,
      description: panel.description ?? '',
      modality: panel.modality,
      testCodes: panel.testCodes,
    });
    setEditorError(null);
    setEditorOpen(true);
    void ensureTestsLoaded();
  };

  const handleDelete = async (panel: IdexxLabPanel) => {
    try {
      await idexxService.deletePanel(panel.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete panel');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setEditorError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        modality: form.modality,
        testCodes: form.testCodes,
      };
      if (editing) {
        await idexxService.updatePanel(editing.id, payload);
      } else {
        await idexxService.createPanel(payload);
      }
      setEditorOpen(false);
      await load();
    } catch (err: any) {
      setEditorError(err.response?.data?.message || 'Failed to save panel');
    } finally {
      setSaving(false);
    }
  };

  // Picker options scoped to the chosen modality (IHD_ = in-house).
  const modalityOptions = useMemo(
    () =>
      testOptions.filter((t) =>
        form.modality === 'INHOUSE'
          ? t.code.startsWith('IHD_')
          : !t.code.startsWith('IHD_'),
      ),
    [testOptions, form.modality],
  );
  // Resolve current selection to option objects (fall back to bare code chips
  // for codes not in the loaded list, e.g. when cloning before tests load).
  const selectedOptions = useMemo(
    () =>
      form.testCodes.map(
        (code) =>
          modalityOptions.find((t) => t.code === code) ??
          ({ code, name: code } as IdexxRefTest),
      ),
    [form.testCodes, modalityOptions],
  );

  const systemPanels = panels.filter((p) => p.isSystem);
  const clinicPanels = panels.filter((p) => !p.isSystem);

  const canSave = form.name.trim().length > 0;

  if (unauthorized) return null;

  const renderPanelRow = (p: IdexxLabPanel) => (
    <Box
      key={p.id}
      display="flex"
      alignItems="flex-start"
      gap={1.5}
      py={1}
      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
    >
      {p.modality === 'INHOUSE' ? (
        <BiotechIcon color="primary" fontSize="small" sx={{ mt: 0.5 }} />
      ) : (
        <ScienceIcon color="primary" fontSize="small" sx={{ mt: 0.5 }} />
      )}
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="body2" fontWeight={600}>
            {p.name}
          </Typography>
          <Chip
            label={p.modality === 'INHOUSE' ? 'In-house' : 'Reference Lab'}
            size="small"
            variant="outlined"
          />
          {p.isSystem && <Chip label="Starter" size="small" color="info" variant="outlined" />}
        </Box>
        {p.description && (
          <Typography variant="caption" color="text.secondary" component="div">
            {p.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" component="div">
          {p.tests.map((t) => t.name).join(' · ') || 'No tests'}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Duplicate">
          <IconButton size="small" onClick={() => openClone(p)}>
            <CloneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!p.isSystem && (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(p)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDelete(p)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    </Box>
  );

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="h6">IDEXX lab panels</Typography>
            <Typography variant="body2" color="text.secondary">
              Reusable test bundles for one-click ordering. Starters are
              ready-to-use; duplicate or add your own.
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openCreate()}>
            New panel
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading panels…</Typography>
          </Box>
        ) : (
          <>
            {clinicPanels.length > 0 && (
              <Box mb={1}>
                <Typography variant="overline" color="text.secondary">Your panels</Typography>
                {clinicPanels.map(renderPanelRow)}
              </Box>
            )}
            <Box>
              <Typography variant="overline" color="text.secondary">Starter panels</Typography>
              {systemPanels.map(renderPanelRow)}
            </Box>
          </>
        )}
      </CardContent>

      <Dialog open={editorOpen} onClose={saving ? undefined : () => setEditorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit panel' : 'New panel'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {editorError && <Alert severity="error">{editorError}</Alert>}
            <TextField
              label="Panel name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
              autoFocus
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>Modality</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={form.modality}
                onChange={(_, v) => v && setForm({ ...form, modality: v as Modality, testCodes: [] })}
              >
                <ToggleButton value="INHOUSE">In-House (IVLS)</ToggleButton>
                <ToggleButton value="REFLAB">Reference Lab</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Autocomplete
              multiple
              options={modalityOptions}
              value={selectedOptions}
              onChange={(_, v) => setForm({ ...form, testCodes: v.map((t) => t.code) })}
              getOptionLabel={(t) => t.name}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              loading={loadingTests}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tests"
                  helperText={loadingTests ? 'Loading test catalog…' : `${modalityOptions.length} tests available`}
                />
              )}
            />
            <TextField
              label="Description (optional)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save' : 'Create panel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default IdexxPanelManager;
